import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { loadingSelector } from 'src/selectors/loading.selector'
import { handlerError } from 'src/selectors/error.selector'
import { showAlert } from 'src/actions/alert.actions'
import {
  actionTypes,
  getDetailedExcelByWarehouse,
  getExcelByWarehouse,
  getInventoryManagementReport,
} from 'src/actions/warehouse.actions'

const EXPORT_ERROR_MESSAGE = 'No se pudo generar el reporte. Intenta de nuevo.'

/** Filtros activos en pantalla que viajan como query params al backend. */
export interface InventoryExportParams {
  search?: string
  categorization?: string | null
  [key: string]: unknown
}

// Los creadores de thunk viven en JS (untyped); el retorno se maneja como
// `any` en el borde de interop y se valida con `result?.ok`.
type ReportThunk = (warehouseId: number, params?: InventoryExportParams) => any

/**
 * Encapsula las descargas de inventario por bodega. Cada handler despacha el
 * thunk, espera el resultado y muestra la alerta de error si falla (respuesta
 * no-2xx). El estado de carga es compartido por los tres reportes.
 */
export const useInventoryExport = (
  warehouseId: number | null,
  params?: InventoryExportParams,
) => {
  const dispatch = useAppDispatch()

  const loading = useAppSelector(state =>
    loadingSelector([
      actionTypes.GET_EXCEL_BY_WAREHOUSE,
      actionTypes.GET_DETAILED_EXCEL_BY_WAREHOUSE,
      actionTypes.GET_INVENTORY_MANAGEMENT_REPORT,
    ])(state),
  )

  const runExport = async (thunk: ReportThunk) => {
    if (warehouseId == null) return
    const result = await dispatch(thunk(warehouseId, params))
    if (!result?.ok) dispatch(showAlert(handlerError(EXPORT_ERROR_MESSAGE)))
  }

  return {
    loading,
    exportInventory: () => runExport(getExcelByWarehouse),
    exportDetailed: () => runExport(getDetailedExcelByWarehouse),
    exportManagement: () => runExport(getInventoryManagementReport),
  }
}
